import { makeStyles, tokens, Text, Button } from '@fluentui/react-components';
import {
  LockClosedRegular,
  SearchRegular,
  ErrorCircleRegular,
  ArrowClockwiseRegular,
  HomeRegular,
} from '@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '@/services/apiError';

type ResourceType = 'project' | 'application' | 'workflow' | 'connection' | 'resource';

interface ResourceErrorPageProps {
  /** The type of resource that failed to load */
  resourceType: ResourceType;
  /** The error that occurred */
  error?: ApiError | Error | null;
  /** Callback to retry the failed operation */
  onRetry?: () => void;
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    padding: tokens.spacingVerticalXXXL,
    gap: tokens.spacingVerticalL,
    textAlign: 'center',
  },
  icon: {
    fontSize: '48px',
    color: tokens.colorNeutralForeground3,
  },
  iconForbidden: {
    fontSize: '48px',
    color: tokens.colorPaletteRedForeground1,
  },
  title: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  message: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
    maxWidth: '480px',
    lineHeight: tokens.lineHeightBase300,
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalM,
  },
});

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function getErrorDetails(resourceType: ResourceType, error?: ApiError | Error | null) {
  if (error instanceof ApiError) {
    if (error.isForbidden || error.isUnauthorized) {
      return {
        title: 'Access Denied',
        message: `You don't have permission to access this ${resourceType}. Contact your administrator to request access, or verify that the ${resourceType} exists.`,
        icon: 'forbidden' as const,
      };
    }
    if (error.isNotFound) {
      return {
        title: `${capitalize(resourceType)} Not Found`,
        message: `This ${resourceType} doesn't exist or may have been deleted. Check the name and try again.`,
        icon: 'notFound' as const,
      };
    }
  }

  return {
    title: 'Something Went Wrong',
    message: `Failed to load this ${resourceType}. Please try again later.`,
    icon: 'error' as const,
  };
}

export const ResourceErrorPage: React.FC<ResourceErrorPageProps> = ({ resourceType, error, onRetry }) => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { title, message, icon } = getErrorDetails(resourceType, error);

  const IconComponent = icon === 'forbidden' ? LockClosedRegular
    : icon === 'notFound' ? SearchRegular
    : ErrorCircleRegular;

  const iconClass = icon === 'forbidden' ? styles.iconForbidden : styles.icon;

  return (
    <div className={styles.container} role="alert">
      <IconComponent className={iconClass} />
      <Text className={styles.title}>{title}</Text>
      <Text className={styles.message}>{message}</Text>
      <div className={styles.actions}>
        {onRetry && (
          <Button appearance="primary" icon={<ArrowClockwiseRegular />} onClick={onRetry}>
            Try Again
          </Button>
        )}
        <Button appearance="subtle" icon={<HomeRegular />} onClick={() => navigate('/?tab=projects')}>
          Go to Projects
        </Button>
      </div>
    </div>
  );
};
